import { useState } from 'react'
import { ArrowRight, ChevronDown, ChevronUp, Mail } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useActorName } from '@/hooks/use-actor-name'
import { MessageEmailBody } from '@/features/communications/attachments'
import { stripQuotedHtml, stripQuotedText } from '@/lib/email-body'
import { cn, formatDateOnly, formatEventTime } from '@/lib/utils'
import type { components } from '@/api/schema'
import { useShipmentHistory } from './hooks'

type HistoryEntry = components['schemas']['ShipmentHistoryEntryRead']
type HistoryEmail = components['schemas']['ShipmentHistoryEmailRead']

/** Recent changes show at once; older ones open on request. */
const RECENT_ENTRIES = 3

const FIELD_LABELS: Record<string, string> = {
  estimated_ship_date: 'Est. ship',
  confirmed_ship_date: 'Confirmed ship',
  actual_ship_date: 'Shipped',
  estimated_delivery_date: 'Est. delivery',
  pickup_date: 'Pickup',
  carrier_name: 'Carrier',
  tracking_numbers: 'Tracking',
  pro_number: 'PRO',
}
const DATE_FIELDS = new Set([
  'estimated_ship_date',
  'confirmed_ship_date',
  'actual_ship_date',
  'estimated_delivery_date',
  'pickup_date',
])
const MONO_FIELDS = new Set(['tracking_numbers', 'pro_number'])

/** Automations that changed a shipment, named the way staff know them. */
const SOURCE_LABELS: Record<string, string> = {
  carrier_tracking_poll: 'Carrier tracking',
  fulfill_shipment: 'Shopify fulfillment',
}

function displayValue(field: string, value: string) {
  if (!DATE_FIELDS.has(field)) return value
  const [year, month, day] = value.split('-').map(Number)
  if (!year || !month || !day) return formatDateOnly(value)
  return new Date(year, month - 1, day).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(year === new Date().getFullYear() ? {} : { year: 'numeric' }),
  })
}

function FieldChange({ change }: { change: HistoryEntry['changes'][number] }) {
  const mono = MONO_FIELDS.has(change.field)
  const value = (text: string, className?: string) => (
    <span className={cn('tabular-nums', mono && 'font-mono text-[13px]', className)}>
      {displayValue(change.field, text)}
    </span>
  )
  return (
    <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0.5">
      <dt className="w-28 shrink-0 text-xs text-muted-foreground">
        {FIELD_LABELS[change.field] ?? change.field}
      </dt>
      <dd className="flex min-w-0 flex-wrap items-baseline gap-x-1.5 break-all text-sm">
        {change.old_value != null && (
          <>
            {value(change.old_value, 'text-muted-foreground line-through decoration-muted-foreground/60')}
            <ArrowRight className="h-3 w-3 shrink-0 self-center text-muted-foreground" aria-label="changed to" />
          </>
        )}
        {change.new_value != null
          ? value(change.new_value, 'font-medium')
          : <span className="text-muted-foreground">Cleared</span>}
      </dd>
    </div>
  )
}

function EmailBehindChange({ email }: { email: HistoryEmail }) {
  const [open, setOpen] = useState(false)
  const sender = email.from_name || email.from_email || 'supplier'
  const html = email.body_html ? stripQuotedHtml(email.body_html) : null
  const text = email.body_text ? stripQuotedText(email.body_text).trim() : ''
  return (
    <div className="space-y-1.5">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        className="inline-flex items-center gap-1 rounded-sm text-xs text-muted-foreground underline decoration-muted-foreground/40 underline-offset-4 hover:text-foreground hover:decoration-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
      >
        <Mail className="h-3.5 w-3.5" aria-hidden />
        Email from {sender}
        {open ? <ChevronUp className="h-3 w-3" aria-hidden /> : <ChevronDown className="h-3 w-3" aria-hidden />}
      </button>
      {open && (
        <div className="rounded-md bg-muted/50 px-3 py-2 text-sm">
          {email.subject && <p className="font-medium break-words">{email.subject}</p>}
          <p className="text-xs text-muted-foreground break-all">
            {email.from_email}
            {email.sent_at && (
              <>
                <span aria-hidden> · </span>
                <time dateTime={email.sent_at}>{formatEventTime(email.sent_at)}</time>
              </>
            )}
          </p>
          <div className="mt-2 max-h-72 overflow-y-auto">
            {html ? (
              <MessageEmailBody html={html} messageUuid={email.uuid} />
            ) : (
              text && <p className="whitespace-pre-line break-words text-foreground/85">{text}</p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function HistoryItem({ entry }: { entry: HistoryEntry }) {
  const actorName = useActorName()
  const who =
    SOURCE_LABELS[entry.actor_name ?? ''] ?? actorName(entry.actor_name) ?? 'System'
  return (
    <li className="relative space-y-1.5 pl-5">
      <span
        className="absolute left-0 top-[5px] h-[7px] w-[7px] rounded-full border border-muted-foreground/60 bg-background"
        aria-hidden
      />
      <p className="text-xs text-muted-foreground">
        <time dateTime={entry.occurred_at}>{formatEventTime(entry.occurred_at)}</time>
        <span aria-hidden> · </span>
        <span className="text-foreground/80">{who}</span>
      </p>
      <dl className="space-y-0.5">
        {entry.changes.map((change) => (
          <FieldChange key={change.field} change={change} />
        ))}
      </dl>
      {entry.reason && (
        <p className="max-w-prose text-sm text-foreground/75 break-words">{entry.reason}</p>
      )}
      {entry.email && <EmailBehindChange email={entry.email} />}
    </li>
  )
}

/** When a shipment's dates, carrier or tracking changed, from what to what, by whom, and why. */
export function ShipmentHistory({ poUuid, shipmentId }: { poUuid: string; shipmentId: number }) {
  const { data, isPending, isError, refetch } = useShipmentHistory(poUuid, shipmentId)
  const [showAll, setShowAll] = useState(false)
  const entries = data ?? []
  const visible = showAll ? entries : entries.slice(0, RECENT_ENTRIES)
  const earlier = entries.length - visible.length

  return (
    <section aria-labelledby={`shipment-history-${shipmentId}`} className="space-y-2.5">
      <h4
        id={`shipment-history-${shipmentId}`}
        className="text-xs font-medium uppercase tracking-wider text-muted-foreground"
      >
        History
      </h4>
      {isPending ? (
        <div className="space-y-2" aria-busy>
          <Skeleton className="h-4 w-48" />
          <Skeleton className="h-4 w-64" />
        </div>
      ) : isError ? (
        <p className="text-sm text-muted-foreground">
          History didn’t load.{' '}
          <Button variant="link" size="sm" className="h-auto p-0" onClick={() => void refetch()}>
            Try again
          </Button>
        </p>
      ) : entries.length === 0 ? (
        <p className="text-sm text-muted-foreground">No date or tracking changes yet.</p>
      ) : (
        <>
          <ol className="relative space-y-4 before:absolute before:bottom-2 before:left-[3px] before:top-2 before:w-px before:bg-border">
            {visible.map((entry) => (
              <HistoryItem key={entry.event_id} entry={entry} />
            ))}
          </ol>
          {(earlier > 0 || showAll) && entries.length > RECENT_ENTRIES && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs text-muted-foreground"
              onClick={() => setShowAll((current) => !current)}
              aria-expanded={showAll}
            >
              {showAll ? (
                <>
                  <ChevronUp className="mr-1 h-3.5 w-3.5" />
                  Show recent only
                </>
              ) : (
                <>
                  <ChevronDown className="mr-1 h-3.5 w-3.5" />
                  Show {earlier} earlier
                </>
              )}
            </Button>
          )}
        </>
      )}
    </section>
  )
}
